import { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { VERIFICATION_CONFIG, UI_CONFIG } from '../config/authConfig';

/**
 * Hook personalizado para gerenciamento inteligente de autenticação
 * Implementa verificação adaptativa e experiência de usuário otimizada
 */
export const useSmartAuth = () => {
  const [sessionHealth, setSessionHealth] = useState({
    isValid: false,
    expiresIn: null,
    lastChecked: null,
    renewalAttempts: 0
  });

  const [userActivity, setUserActivity] = useState({
    isActive: true,
    lastActivity: Date.now(),
    awayTime: 0
  });

  // Verificação silenciosa de token sem interrupção do usuário
  const silentTokenCheck = useCallback(async () => {
    try {
      const user = authService.getCurrentUser();
      if (!user?.token) {
        setSessionHealth(prev => ({ ...prev, isValid: false }));
        return false;
      }

      // Verificar se token está próximo de expirar
      const tokenData = JSON.parse(atob(user.token.split('.')[1]));
      const expirationTime = tokenData.exp * 1000;
      const timeUntilExpiry = expirationTime - Date.now();
      
      // Se token vai expirar em menos de 10 minutos, tentar renovar silenciosamente
      if (timeUntilExpiry < VERIFICATION_CONFIG.CHECK_INTERVAL) {
        try {
          await authService.refreshToken();
          setSessionHealth(prev => ({
            ...prev,
            isValid: true,
            expiresIn: expirationTime,
            lastChecked: Date.now(),
            renewalAttempts: 0
          }));
          return true;
        } catch (error) {
          console.warn('Falha na renovação silenciosa do token:', error);
          setSessionHealth(prev => ({
            ...prev,
            renewalAttempts: prev.renewalAttempts + 1
          }));
          
          // Se falhou muitas vezes, pode ser necessário reautenticar
          if (sessionHealth.renewalAttempts >= 3) {
            return false;
          }
        }
      }

      setSessionHealth(prev => ({
        ...prev,
        isValid: true,
        expiresIn: expirationTime,
        lastChecked: Date.now()
      }));
      
      return true;
    } catch (error) {
      console.error('Erro na verificação silenciosa:', error);
      setSessionHealth(prev => ({ ...prev, isValid: false }));
      return false;
    }
  }, [sessionHealth.renewalAttempts]);

  // Monitoramento de atividade do usuário
  useEffect(() => {
    let activityTimeout;
    let awayTimeout;

    const updateActivity = () => {
      const now = Date.now();
      setUserActivity(prev => ({
        ...prev,
        isActive: true,
        lastActivity: now,
        awayTime: 0
      }));

      // Reiniciar contador de tempo ausente
      clearTimeout(awayTimeout);
      awayTimeout = setTimeout(() => {
        setUserActivity(prev => ({
          ...prev,
          isActive: false,
          awayTime: Date.now() - prev.lastActivity
        }));
      }, VERIFICATION_CONFIG.INACTIVITY_THRESHOLD);
    };

    const handleActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(updateActivity, VERIFICATION_CONFIG.ACTIVITY_DEBOUNCE);
    };

    // Registrar listeners para atividade
    VERIFICATION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Verificação inicial
    updateActivity();

    return () => {
      clearTimeout(activityTimeout);
      clearTimeout(awayTimeout);
      VERIFICATION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  // Verificação adaptativa baseada na atividade do usuário
  useEffect(() => {
    const getAdaptiveInterval = () => {
      if (!userActivity.isActive) {
        // Usuário inativo - verificar menos frequentemente
        return VERIFICATION_CONFIG.BACKGROUND_CHECK_INTERVAL;
      }
      return VERIFICATION_CONFIG.CHECK_INTERVAL;
    };

    let checkTimeout;

    const scheduleNextCheck = () => {
      const interval = getAdaptiveInterval();
      checkTimeout = setTimeout(async () => {
        await silentTokenCheck();
        scheduleNextCheck(); // Reagendar
      }, interval);
    };

    // Iniciar verificação
    scheduleNextCheck();

    return () => {
      clearTimeout(checkTimeout);
    };
  }, [userActivity.isActive, silentTokenCheck]);

  // Verificação quando a janela ganha foco
  useEffect(() => {
    const handleFocus = async () => {
      // Quando usuário volta para a aplicação, verificar imediatamente
      await silentTokenCheck();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [silentTokenCheck]);

  // Retornar estados e funções úteis
  return {
    sessionHealth,
    userActivity,
    silentTokenCheck,
    
    // Funções utilitárias
    isSessionHealthy: sessionHealth.isValid && sessionHealth.renewalAttempts < 3,
    shouldShowWarning: sessionHealth.expiresIn && 
                      (sessionHealth.expiresIn - Date.now()) < UI_CONFIG.VISUAL_FEEDBACK.WARNING_THRESHOLD,
    timeSinceLastActivity: Date.now() - userActivity.lastActivity,
    
    // Função para forçar verificação manual
    forceCheck: silentTokenCheck
  };
};

export default useSmartAuth;
